// Data hooks for the operator notification/acceptance surface.
//
// REALTIME (WS, never polling — operator-ui rule `feedback_realtime_websockets_
// not_polling`): the status rides the operator-bff `/cases` WS gateway. There is
// no timer-driven refetch and no interval anywhere here. Instead `useCaseNotifications`
// attaches its own listeners to the shared Socket.IO singleton (`getSocket()`)
// for the case-scoped write events the BFF already emits — `CaseChanged`,
// `BookingConfirmed`, `PassengerStatusChanged` — and, on a matching `caseUrn`,
// INVALIDATES the notifications query so it refetches once (refetch-on-event,
// targeted; the central `applyEvent` merge in lib/ws is untouched). The case
// room is already joined by the case-detail route (`joinCase`).
//
// React Query dedups the query across every PnrRow that calls this for the same
// case, so N rows = ONE network request and ONE WS subscription path.
//
// FIX #609: WS listeners are now reference-counted at the module level so that
// N hook instances share ONE set of listeners per caseUrn. Without this, each
// instance registered its own listeners, causing N invalidateQueries calls per
// event — and while React Query deduplicates the HTTP fetch, the invalidation
// storm still triggered unnecessary state transitions and could cascade into
// extra refetches after mutations.

import { useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { getSocket } from '@/lib/ws/socket';
import { forceAccept, getCaseNotifications, resendNotification } from './api';
import type {
  IForceAcceptBody,
  IForceAcceptResult,
  INotificationCaseStatus,
  INotificationStatusRow,
  ISendNotificationBody,
  ISendNotificationResult,
} from './types';

/** TanStack Query key for a case's notification status. */
export function notificationsKey(caseUrn: string): readonly [string, string] {
  return ['notifications', caseUrn];
}

// Case-scoped WS events whose arrival means an acceptance/delivery state for this
// case may have moved (the gate transitions ride these). The BFF emits each as a
// Socket.IO event named by its `type`, with the rest of the envelope as payload.
const CASE_WS_EVENTS = ['CaseChanged', 'BookingConfirmed', 'PassengerStatusChanged'] as const;

// ────────────────────────────────────────────────────────────────────────────────
// Shared WS subscription registry (module-scoped, reference-counted).
// One subscription per caseUrn, shared across ALL hook instances that request it.
// ────────────────────────────────────────────────────────────────────────────────

interface CaseSubscription {
  refCount: number;
  onCaseEvent: (payload: unknown) => void;
  onReconnect: () => void;
}

const subscriptions = new Map<string, CaseSubscription>();

function subscribeToCaseWs(caseUrn: string, qc: QueryClient): void {
  const existing = subscriptions.get(caseUrn);
  if (existing) {
    existing.refCount += 1;
    return;
  }

  const socket = getSocket();
  if (!socket) return;

  const onCaseEvent = (payload: unknown): void => {
    if (
      payload &&
      typeof payload === 'object' &&
      (payload as { caseUrn?: unknown }).caseUrn === caseUrn
    ) {
      void qc.invalidateQueries({ queryKey: notificationsKey(caseUrn) });
    }
  };

  const onReconnect = (): void => {
    void qc.invalidateQueries({ queryKey: notificationsKey(caseUrn) });
  };

  socket.on('connect', onReconnect);
  for (const evt of CASE_WS_EVENTS) socket.on(evt, onCaseEvent);

  subscriptions.set(caseUrn, { refCount: 1, onCaseEvent, onReconnect });
}

function unsubscribeFromCaseWs(caseUrn: string): void {
  const sub = subscriptions.get(caseUrn);
  if (!sub) return;

  sub.refCount -= 1;
  if (sub.refCount > 0) return;

  const socket = getSocket();
  if (socket) {
    socket.off('connect', sub.onReconnect);
    for (const evt of CASE_WS_EVENTS) socket.off(evt, sub.onCaseEvent);
  }
  subscriptions.delete(caseUrn);
}

// Exported for testing only — clears all active subscriptions.
export function _clearSubscriptionsForTest(): void {
  for (const [urn] of subscriptions) unsubscribeFromCaseWs(urn);
  subscriptions.clear();
}

// ────────────────────────────────────────────────────────────────────────────────

export interface UseCaseNotificationsResult {
  /** All PNR-group status rows for the case (empty while loading / on error). */
  items: INotificationStatusRow[];
  deliveryStatusAvailable?: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  /** Look up the status row for a single PNR group (sub-case). */
  rowFor: (subCaseUrn: string) => INotificationStatusRow | undefined;
}

export function useCaseNotifications(
  caseUrn: string,
  options: { enabled?: boolean } = {},
): UseCaseNotificationsResult {
  const enabled = (options.enabled ?? true) && !!caseUrn;
  const qc = useQueryClient();
  const subscribedRef = useRef(false);

  const query = useQuery<INotificationCaseStatus>({
    queryKey: notificationsKey(caseUrn),
    queryFn: () => getCaseNotifications(caseUrn),
    enabled,
    staleTime: 30_000,
  });

  // Live updates over the WS gateway — reference-counted shared subscription.
  useEffect(() => {
    if (!enabled) return;

    subscribeToCaseWs(caseUrn, qc);
    subscribedRef.current = true;

    return () => {
      if (subscribedRef.current) {
        unsubscribeFromCaseWs(caseUrn);
        subscribedRef.current = false;
      }
    };
  }, [caseUrn, enabled, qc]);

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const bySubCase = useMemo(() => {
    const map = new Map<string, INotificationStatusRow>();
    for (const row of items) map.set(row.subCaseUrn, row);
    return map;
  }, [items]);

  return {
    items,
    ...(query.data?.deliveryStatusAvailable !== undefined
      ? { deliveryStatusAvailable: query.data.deliveryStatusAvailable }
      : {}),
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
    rowFor: (subCaseUrn: string) => bySubCase.get(subCaseUrn),
  };
}

/** Resend / re-dispatch an offer notification (optionally choosing the channel). */
export function useResendNotification(caseUrn: string) {
  const qc = useQueryClient();
  return useMutation<ISendNotificationResult, unknown, ISendNotificationBody>({
    mutationKey: ['notification-send', caseUrn],
    mutationFn: (body: ISendNotificationBody) => resendNotification(caseUrn, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationsKey(caseUrn) });
    },
  });
}

/** Operator force-accept for a single PNR group (supervisor+; gate enforced by the BFF/cases). */
export function useForceAccept(caseUrn: string) {
  const qc = useQueryClient();
  return useMutation<IForceAcceptResult, unknown, { subCaseUrn: string; body: IForceAcceptBody }>({
    mutationFn: ({ subCaseUrn, body }) => forceAccept(caseUrn, subCaseUrn, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationsKey(caseUrn) });
    },
  });
}
